import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners, provideAppInitializer, inject, effect, APP_BOOTSTRAP_LISTENER } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideRouter, TitleStrategy, withDisabledInitialNavigation } from '@angular/router';
import { TranslateTitleStrategy } from './Modules/Shared/Strategies/translate-title.strategy';
import { routes } from './routes';
import { provideHttpClient, withFetch, withInterceptors, HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_NATIVE_DATE_FORMATS,
} from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslatePaginatorIntlStrategy } from './Modules/Shared/Strategies/translate-paginator-intl.strategy';
import { httpLoaderFactory } from './Modules/Shared/Factories/translate-loader.factory';
import { tokenInterceptor } from './Modules/Shared/Interceptors/token.interceptor';
import { loadingInterceptor } from './Modules/Shared/Interceptors/loading.interceptor';
import { blockingWaitInterceptor } from './Modules/Shared/Interceptors/blocking-wait.interceptor';
import { errorInterceptor } from './Modules/Shared/Interceptors/error.interceptor';
import {
  initializeAppWhenSupported,
  startNavigationWhenSupported,
} from './Modules/Shared/Factories/deferred-boot.factory';
import { TranslationService } from './Modules/Shared/Services/translation.service';
import { RouteReuseStrategy } from '@angular/router';
import { ParamChangeReuseStrategy } from './Modules/Shared/Strategies/param-change-reuse.strategy';
import { BootSplashService } from './Modules/Shared/Services/boot-splash.service';
import { EuropeanDateAdapter, toDateLocale } from './Modules/Shared/Strategies/european-date.adapter';

registerLocaleData(localeFr);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      inject(BootSplashService);
    }),

    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      useFactory: () => {
        const bootSplash = inject(BootSplashService);
        return () => bootSplash.notifyBootstrapped();
      },
    },

    { provide: APP_BOOTSTRAP_LISTENER, multi: true, useFactory: startNavigationWhenSupported },

    { provide: RouteReuseStrategy, useClass: ParamChangeReuseStrategy },
    provideAppInitializer(() => {
      const lang = inject(TranslationService).resolveColdLoadLanguage();
      return firstValueFrom(inject(TranslateService).use(lang));
    }),

    provideAppInitializer(initializeAppWhenSupported),

    provideAppInitializer(() => {
      const dateAdapter = inject(DateAdapter);
      const translationService = inject(TranslationService);
      effect(() => dateAdapter.setLocale(toDateLocale(translationService.currentLanguage())));
    }),

    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withDisabledInitialNavigation()),
    { provide: TitleStrategy, useClass: TranslateTitleStrategy },
    { provide: MatPaginatorIntl, useClass: TranslatePaginatorIntlStrategy },
    { provide: DateAdapter, useClass: EuropeanDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => toDateLocale(inject(TranslationService).currentLanguage()),
    },
    provideHttpClient(
      withFetch(),
      withInterceptors([blockingWaitInterceptor, tokenInterceptor, loadingInterceptor, errorInterceptor])
    ),

    importProvidersFrom([
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient]
        }
      }),
    ]),

  ]
};
